/**
 * The languages FEATit speaks (`docs/concept-internationalisation.md` §3.3, ADR-148).
 *
 * One list, so a third language costs its translations and one entry here (IN-12): no other file spells
 * the supported languages out. `en` is the source and the fallback. No enum, as everywhere in this package.
 */
export const SUPPORTED_LOCALES = ['en', 'de'] as const;
export type LocaleType = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: LocaleType = 'en';

/**
 * The pseudo-locale (§6.3): the English catalogue accented, a third longer and wrapped in ⟦ ⟧, so a text
 * that bypassed the catalogue or overflows its control is visible. Selectable in development and in end
 * to end tests only; never offered to a person and never in `SUPPORTED_LOCALES`.
 */
export const PSEUDO_LOCALE = 'en-XA';
export type PseudoLocaleType = typeof PSEUDO_LOCALE;

/**
 * What each language is called in itself (concept §4.3): the Settings choice names every language in its own,
 * so a person who cannot read the current one can still find theirs. Names, not messages: not translated.
 */
export const LOCALE_NAMES: Readonly<Record<LocaleType, string>> = { en: 'English', de: 'Deutsch' };

/**
 * Languages offered as a **preview** (concept §3.4): chosen by a person, never by default, and labelled so in
 * the choice, in their own language ("Deutsch (Vorschau)"). German is a preview until BOTH of §3.4's conditions
 * hold: the ratchet's baseline has no literal left in the core areas (true since 2026-10-25) and Jay has reviewed
 * the German (`GERMAN_REVIEWED_BY_JAY`). `web/src/i18n/localePreview.guard.test.ts` holds the label both ways.
 */
export const LOCALE_PREVIEW_SUFFIX: Readonly<Partial<Record<LocaleType, string>>> = {
  de: ' (Vorschau)',
};

/**
 * §3.4's second condition: Jay has reviewed the German (D-0215). His answer was "get it online and allow me to
 * switch via user settings, then I'll review"; that review is T-0482 and is still open. Set this to `true` in the
 * same edit that drops `de` from `LOCALE_PREVIEW_SUFFIX`, when T-0482 closes.
 */
export const GERMAN_REVIEWED_BY_JAY = false;

/** What the Settings choice calls a language: its own name, and "preview" in its own language while it is one. */
export function localeChoiceLabel(locale: LocaleType): string {
  return `${LOCALE_NAMES[locale]}${LOCALE_PREVIEW_SUFFIX[locale] ?? ''}`;
}

/**
 * The regions a person may pick for dates and numbers (F-0615, concept §5.6), per language, most used first.
 * To add one, append its BCP 47 tag (`<language>-<REGION>`) to its language's list; nothing else names them.
 */
export const FORMAT_REGIONS: Readonly<Record<LocaleType, readonly string[]>> = {
  en: ['en-US', 'en-GB', 'en-IE', 'en-AU', 'en-NZ', 'en-CA', 'en-IN', 'en-ZA'],
  de: ['de-DE', 'de-AT', 'de-CH', 'de-LU', 'de-LI'],
};

export function isSupportedLocale(value: unknown): value is LocaleType {
  return (SUPPORTED_LOCALES as readonly unknown[]).includes(value);
}

/**
 * How compact a relative time is in each language (T-0806, concept §5.6): `narrow` unless a language names
 * another. German is `short` ("vor 6 Min."), because CLDR's narrow German minute is a bare "m".
 */
export const RELATIVE_TIME_STYLE: Readonly<
  Partial<Record<LocaleType, Intl.RelativeTimeFormatStyle>>
> = { de: 'short' };

export function relativeTimeStyle(locale: string): Intl.RelativeTimeFormatStyle {
  return RELATIVE_TIME_STYLE[locale as LocaleType] ?? 'narrow';
}
